import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { suppliersApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { useModuleScopedBreadcrumbs } from '@/hooks/useModuleScopedBreadcrumbs';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import type { SupplierRequest, SupplierResponse } from '@/types/supplier';
import { formatCurrency } from '@/utils/format';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const empty: SupplierRequest = { name: '' };

export function SuppliersPage() {
  const breadcrumbs = useModuleScopedBreadcrumbs('Suppliers');
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<SupplierResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SupplierResponse | null>(null);
  const [form, setForm] = useState<SupplierRequest>(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await suppliersApi.list({ page: pagination.page, size: pagination.size, search: debouncedSearch || undefined });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) { showApiError(err, 'Failed to load'); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => { load(); }, [load]);

  const columns: Column<SupplierResponse>[] = [
    { key: 'name', header: 'Name' },
    { key: 'phone', header: 'Phone' },
    { key: 'outstandingBalance', header: 'Outstanding', render: (r) => formatCurrency(r.outstandingBalance) },
    { key: 'edit', header: '', render: (r) => (
      <button type="button" className="text-primary-600" onClick={() => { setEditing(r); setForm({ name: r.name, phone: r.phone, email: r.email, address: r.address, gstNumber: r.gstNumber }); setModalOpen(true); }}>Edit</button>
    )},
  ];

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await suppliersApi.update(editing.id, form);
      else await suppliersApi.create(form);
      notify.success('Saved');
      setModalOpen(false);
      load();
    } catch (err) { showApiError(err, 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Suppliers" breadcrumbs={breadcrumbs}
        actions={<Button onClick={() => { setEditing(null); setForm(empty); setModalOpen(true); }}><AddIcon fontSize="small" /> Add</Button>} />
      <DataTable columns={columns} data={data} keyField="id" loading={loading} search={pagination.search}
        onSearchChange={(v) => { pagination.setSearch(v); pagination.resetPage(); }}
        page={pagination.page} totalPages={pagination.totalPages} onPageChange={pagination.setPage} />
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit' : 'New Supplier'}
        footer={<><Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button><Button onClick={save} loading={saving}>Save</Button></>}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Phone" value={form.phone ?? ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
      </Modal>
    </div>
  );
}
